import { useSelector } from "react-redux";
import { useAccount, useReadContracts } from "wagmi";
import useDappConfig from "./useDappConfig";
import { toFixedWithoutRounding } from "@constants/common";
import { formatUnits, maxUint256 } from "viem";
import { ETH } from "@config/contractsConfig";
import { useEffect, useMemo } from "react";

const useAllowance = () => {
  const { selectedERC20 } = useSelector((state: any) => state.user);
  const { isTransactionProcessing } = useSelector((state: any) => state.stake);
  const { address: walletAddress } = useAccount();

  const { config, isWithdrawSupported } = useDappConfig();

  const token = config.tokens[selectedERC20];

  const allowanceContracts = useMemo(() => {
    const contracts = [];

    if (selectedERC20 !== ETH) {
      contracts.push({
        ...token,
        functionName: "allowance",
        args: [walletAddress, config.interactionPools.lrtDepositPool.address],
      });
    }

    if (isWithdrawSupported) {
      contracts.push({
        ...config.xtoken,
        functionName: "allowance",
        args: [
          walletAddress,
          config.interactionPools.lrtWithdrawalManager?.address,
        ],
      });
    }

    return contracts;
  }, [token, selectedERC20, isWithdrawSupported, walletAddress]);

  const { data, refetch: refetchAllowance } = useReadContracts({
    contracts: [...allowanceContracts],
    query: {
      enabled: !!walletAddress,
      refetchInterval: 60_000,
    },
  });

  useEffect(() => {
    if (!isTransactionProcessing) {
      refetchAllowance();
    }
  }, [isTransactionProcessing]);

  const allowanceData = useMemo(() => {
    if (data && walletAddress) {
      if (selectedERC20 === ETH && !isWithdrawSupported) {
        return {
          erc20Allowance: 0,
          xTokenAllowance: 0,
        };
      } else if (selectedERC20 === ETH) {
        return {
          erc20Allowance: +toFixedWithoutRounding(
            formatUnits(maxUint256, config.nativeToken.decimals),
            8
          ),
          xTokenAllowance: +toFixedWithoutRounding(
            data
              ? formatUnits(data[0]?.result as bigint, config.xtoken.decimals)
              : "0",
            8
          ),
        };
      }
      return {
        erc20Allowance: +toFixedWithoutRounding(
          formatUnits(
            (data[0]?.result as bigint) || BigInt(0),
            config.nativeToken.decimals
          ),
          8
        ),
        xTokenAllowance: isWithdrawSupported
          ? +toFixedWithoutRounding(
              data
                ? formatUnits(
                    (data[1]?.result as bigint) || BigInt(0),
                    config.xtoken.decimals
                  )
                : "0",
              8
            )
          : 0,
      };
    }
    return {
      erc20Allowance: 0,
      xTokenAllowance: 0,
    };
  }, [data, walletAddress, selectedERC20, isWithdrawSupported]);

  return allowanceData;
};

export default useAllowance;
